import { normalizeForSearch } from './search-normalize.js';

/**
 * Plaats-zoeken op genormaliseerde naam + aliassen — **één bron van waarheid** zodat
 * de Fastify-SSR (`/zoek`) en de client-side combobox-island identiek matchen (geen
 * drift). Werkt op de gebakken plaatsenlijst; geen externe call (§6).
 *
 * Een volledige `Place` voldoet aan `SearchPlace`; de client krijgt een getrimde index.
 */

export interface SearchPlace {
  slug: string;
  name: string;
  province: string;
  aliases: string[];
  /** Coördinaten — optioneel; aanwezig in de gebakken index t.b.v. geolocatie (nearest-place). */
  lat?: number;
  lon?: number;
}

/**
 * Ranking (laag = beter): exacte naam → naam-prefix → naam-bevat → alias-prefix →
 * alias-bevat, daarna alfabetisch (NL). Lege query → geen resultaten.
 */
/** Max query-lengte: een plaatsnaam + provincie past hier ruim in. */
export const MAX_QUERY_LEN = 64;

export function matchPlaces<T extends SearchPlace>(
  places: readonly T[],
  query: string,
  limit = 8,
): T[] {
  // F10: kap de query af vóór het matchen — voorkomt per-request CPU-amplificatie over
  // ~2500 plaatsen × alias-lus bij een extreem lange `q`. Client-island erft de grens.
  const q = normalizeForSearch(query.slice(0, MAX_QUERY_LEN));
  if (!q) return [];

  const scored: { place: T; score: number }[] = [];
  for (const place of places) {
    const name = normalizeForSearch(place.name);
    let score = -1;
    if (name === q) score = 0;
    else if (name.startsWith(q)) score = 1;
    else if (name.includes(q)) score = 2;
    else {
      // Aliassen: prefix (3) wint van louter bevatten (4).
      for (const alias of place.aliases) {
        const a = normalizeForSearch(alias);
        if (a.startsWith(q)) {
          score = 3;
          break;
        }
        if (a.includes(q) && score < 0) score = 4;
      }
    }
    if (score >= 0) scored.push({ place, score });
  }

  scored.sort((a, b) => a.score - b.score || a.place.name.localeCompare(b.place.name, 'nl'));
  return scored.slice(0, limit).map((s) => s.place);
}

/**
 * Dichtstbijzijnde plaats bij coördinaten (browser-geolocatie → plaats). Werkt **client-side** op de
 * gebakken index met lat/lon, zodat de locatie van de gebruiker het toestel niet verlaat (privacy).
 * Equirectangulaire benadering (snel, ruim voldoende op NL-schaal); entries zonder coördinaten worden
 * overgeslagen. `null` als er niets bruikbaars is.
 */
export function nearestPlace<T extends SearchPlace>(
  places: readonly T[],
  lat: number,
  lon: number,
): T | null {
  const kx = Math.cos((lat * Math.PI) / 180); // lengtegraad schalen op deze breedtegraad
  let best: T | null = null;
  let bestDist = Number.POSITIVE_INFINITY;
  for (const place of places) {
    if (typeof place.lat !== 'number' || typeof place.lon !== 'number') continue;
    const dLat = place.lat - lat;
    const dLon = (place.lon - lon) * kx;
    const dist = dLat * dLat + dLon * dLon;
    if (dist < bestDist) {
      bestDist = dist;
      best = place;
    }
  }
  return best;
}
